import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';

const { FiChevronLeft, FiChevronRight, FiCalendar, FiPackage, FiTruck, FiActivity } = FiIcons;

const ProductionCalendar = ({ orders, varieties, batches }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });

  const allEvents = orders.flatMap(order => {
    return order.items.flatMap((item, itemIdx) => {
      const variety = varieties.find(v => v.id === item.varietyId);
      if (!variety) return [];

      // Handle Sudden Orders: No sowing event, only delivery
      if (order.isSudden) {
        const deliveryDate = new Date(order.baseDate);
        const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
        const batch = batches.find(b => b.taskId === taskId);
        return [
          { date: deliveryDate, type: 'delivery', variety: variety.name, customer: order.customer, completed: !!batch?.delivered, total: item.quantity, isSudden: true }
        ];
      }

      const totalCycle = (variety.growingDays || 0) + (variety.darkTreatmentDays || 0);
      const base = new Date(order.baseDate);
      let sowingDate, deliveryDate;

      if (order.dateType === 'delivery') {
        deliveryDate = base;
        sowingDate = subDays(base, totalCycle);
      } else {
        sowingDate = base;
        deliveryDate = addDays(base, totalCycle);
      }

      const taskId = `${order.id}-${item.varietyId}-${itemIdx}`;
      const batch = batches.find(b => b.taskId === taskId);

      return [
        { date: sowingDate, type: 'sowing', variety: variety.name, customer: order.customer, completed: !!batch?.actualSowingDate, total: (item.quantity + item.bufferQuantity) },
        { date: deliveryDate, type: 'delivery', variety: variety.name, customer: order.customer, completed: !!batch?.delivered, total: item.quantity }
      ];
    });
  });

  const getEventsForDay = (day) => allEvents.filter(e => isSameDay(e.date, day));

  const getEventColor = (type, completed, isSudden) => {
    if (completed) return 'bg-gray-100 text-gray-400 line-through border-gray-200';
    if (isSudden) return 'bg-orange-50 text-orange-700 border-orange-100';
    switch (type) {
      case 'sowing': return 'bg-green-50 text-[#16a34a] border-green-100';
      case 'delivery': return 'bg-blue-50 text-[#1e3a8a] border-blue-100';
      default: return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-10 gap-4">
        <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Schedule</h1>
        <div className="flex items-center bg-white rounded-xl shadow-sm border border-gray-100 p-1.5 w-full md:w-auto">
          <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg transition-all">
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <button onClick={() => setCurrentDate(new Date())} className="flex-1 md:flex-none px-6 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50 rounded-lg uppercase tracking-widest text-center">
            Current
          </button>
          <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="p-2 hover:bg-gray-50 rounded-lg transition-all">
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="p-6 md:p-8 border-b border-gray-100 bg-gray-50/30 flex justify-between items-center">
          <h2 className="text-xl md:text-2xl font-light text-[#1e3a8a] tracking-wide">{format(currentDate, 'MMMM yyyy')}</h2>
        </div>

        <div className="p-4">
          {/* Desktop Grid View */}
          <div className="hidden md:grid grid-cols-7 gap-px bg-gray-100 border border-gray-100 rounded-3xl overflow-hidden">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="bg-gray-50/50 text-center text-xs font-bold text-gray-400 py-4 uppercase tracking-[0.2em]">{d}</div>
            ))}
            {days.map(day => (
              <div key={day.toString()} className={`min-h-[160px] bg-white p-3 relative transition-all hover:z-10 hover:shadow-lg hover:scale-[1.01] ${!isSameMonth(day, currentDate) ? 'bg-gray-50/20' : ''}`}>
                <div className={`text-sm font-medium mb-3 ${isSameDay(day, new Date()) ? 'bg-[#16a34a] text-white w-8 h-8 flex items-center justify-center rounded-full shadow-lg shadow-green-100' : 'text-gray-400'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1.5 overflow-y-auto max-h-[110px] scrollbar-hide">
                  {getEventsForDay(day).map((e, i) => (
                    <div key={i} className={`text-sm p-2 rounded-lg border truncate font-medium shadow-sm flex flex-col ${getEventColor(e.type, e.completed, e.isSudden)}`}>
                      <div className="flex items-center">
                        <span className="mr-1">{e.type === 'sowing' ? '🌱' : (e.isSudden ? '⚡' : '🚚')}</span>
                        <span>{e.variety}</span>
                      </div>
                      <div className="text-xs opacity-85 mt-1 flex justify-between font-normal">
                        <span>{e.customer}</span>
                        <span>{e.total}u</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Agenda View */}
          <div className="md:hidden space-y-4">
            {days.filter(d => getEventsForDay(d).length > 0 && isSameMonth(d, currentDate)).map(day => (
              <div key={day.toString()} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                <div className="flex items-center mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shadow-sm mr-3 ${isSameDay(day, new Date()) ? 'bg-[#16a34a] text-white' : 'bg-white text-gray-700'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{format(day, 'EEEE')}</span>
                    <span className="text-xs text-gray-500">{format(day, 'MMMM yyyy')}</span>
                  </div>
                </div>
                <div className="space-y-2 pl-3 border-l-2 border-gray-200 ml-5">
                  {getEventsForDay(day).map((e, i) => (
                    <div key={i} className={`p-3 rounded-xl border flex flex-col ${getEventColor(e.type, e.completed, e.isSudden)}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center font-bold text-sm">
                          <span className="mr-2 text-lg">{e.type === 'sowing' ? '🌱' : (e.isSudden ? '⚡' : '🚚')}</span>
                          <span>{e.variety}</span>
                        </div>
                        <span className="text-xs font-medium px-2 py-0.5 bg-white/50 rounded-full">{e.total}u</span>
                      </div>
                      <div className="text-xs opacity-80 pl-7">
                        {e.customer}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {days.every(d => getEventsForDay(d).length === 0 || !isSameMonth(d, currentDate)) && (
              <div className="text-center py-10 text-gray-400">
                <SafeIcon icon={FiCalendar} className="text-4xl mb-2 opacity-30 mx-auto" />
                <p className="text-sm">No tasks for this month</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductionCalendar;